import React, { useCallback } from "react";
import { set } from "object-path-immutable";
import Modal from "react-modal";
import Table, { Column } from "components/table/Table";
import Checkbox from "components/checkbox/Checkbox";
import "./AdjustableColumnsTable.scss";
import { MediaSize, Meet } from "types";
import { Alignment } from "react-virtualized";
import toNumber from "lodash/toNumber";
import map from "lodash/map";
import cloneDeep from "lodash/cloneDeep";
import each from "lodash/each";
import find from "lodash/find";
import filter from "lodash/filter";
import round from "lodash/round";
import sumBy from "lodash/sumBy";
import isEqual from "lodash/isEqual";

import { StoredTableData } from "app/meets/display2/DisplaySettingsProvider";

type AdjustColumnsModalProps = {
  changeColumnAttribute: (
    index: number,
    prop: "width" | "visible",
    value: any,
  ) => void;
  onRequestClose: () => void;
  isOpen: boolean;
  allColumns: Column[];
};

function AdjustColumnsModal({
  changeColumnAttribute,
  onRequestClose,
  isOpen,
  allColumns,
}: AdjustColumnsModalProps) {
  const changeWidth = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const value = event.target.value;
    const asNumber = value === "" ? null : toNumber(value);
    changeColumnAttribute(index, "width", asNumber);
  };

  const changeVisibility = (value: boolean, index: number) => {
    changeColumnAttribute(index, "visible", value);
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      contentLabel="Adjust Columns"
      onRequestClose={onRequestClose}
      className={{
        base: "adjust-columns-modal not-draggable",
        afterOpen: "adjust-columns-modal-after-open",
        beforeClose: "adjust-columns-modal-before-close",
      }}
      overlayClassName={{
        base: "modal-overlay not-draggable",
        afterOpen: "",
        beforeClose: "",
      }}
    >
      <div className="content">
        <table className="table">
          <thead>
            <tr>
              <th>Column</th>
              <th>Width</th>
              <th>Visible</th>
            </tr>
          </thead>
          <tbody>
            {map(allColumns, (column, index) => {
              if (column.locked) {
                return null;
              }

              return (
                <tr key={`${column.key}-${index}`}>
                  <td>
                    <label>{column.label}</label>
                  </td>
                  <td>
                    <input
                      type="number"
                      value={column.width === null ? "" : column.width}
                      onChange={(e) => changeWidth(e, index)}
                    />
                  </td>
                  <td>
                    <Checkbox
                      value={!!column.visible}
                      onCheck={(v) => changeVisibility(v, index)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="button-row">
        <button onClick={onRequestClose}>Close</button>
      </div>
    </Modal>
  );
}

type AdjustableColumnsTableProps = {
  columns: Column[];
  storedTableData: StoredTableData;
  data: any;
  getCellLines: (index: number) => number;
  meet: Meet;
  numberOfFixedLeftColumns: number;
  media: MediaSize;
  padBottom?: boolean;
  modalIsOpen: boolean;
  closeModal: () => void;
  scrollToRow?: number;
  scrollToAlignment: Alignment;
  cellStyle?: React.CSSProperties;
  autoWidth?: boolean;
  sizeMultiplier?: number;
  gridAriaLabel: string;
  onChange: (tableData: StoredTableData) => void;
};

const getVisibleColumns = (columns: Column[]) => filter(columns, "visible");

function AdjustableColumnsTableV2({
  columns: baseColumns,
  onChange,
  storedTableData,
  data,
  getCellLines,
  meet,
  numberOfFixedLeftColumns,
  media,
  padBottom,
  modalIsOpen,
  closeModal,
  scrollToRow,
  scrollToAlignment,
  cellStyle,
  autoWidth,
  sizeMultiplier,
  gridAriaLabel,
}: AdjustableColumnsTableProps) {
  const { visibleColumns, allColumns } = React.useMemo(() => {
    const parsedColumns = cloneDeep(baseColumns);

    each(storedTableData, (cConfig) => {
      const column = find(parsedColumns, { key: cConfig.k });

      if (column) {
        column.width = cConfig.w;
        column.visible = !!cConfig.v;
      }
    });

    return {
      visibleColumns: getVisibleColumns(parsedColumns),
      allColumns: parsedColumns,
    };
  }, [baseColumns, storedTableData]);

  const updateOnChanged = useCallback(
    (columnsToStore: Column[]) => {
      const storableTableData = map(columnsToStore, (column) => {
        if (
          isEqual(
            column,
            baseColumns.find((c) => c.key === column.key),
          )
        ) {
          return null;
        }
        return {
          k: column.key,
          w: column.width,
          v: column.visible ? 1 : 0,
        };
      });

      const storableTableDataWithoutNulls = storableTableData.filter(
        (a): a is NonNullable<typeof a> => a !== null && a !== undefined,
      );

      onChange(storableTableDataWithoutNulls);
    },
    [baseColumns, onChange],
  );

  const changeColumnAttribute = (
    columnIndex: number,
    property: "width" | "visible",
    value: any,
  ) => {
    const updatedColumns = set(allColumns, [columnIndex, property], value);

    updateOnChanged(updatedColumns);
  };

  let columns = visibleColumns;
  if (sizeMultiplier) {
    columns = map(columns, (column) => {
      return { ...column, width: round(column.width * sizeMultiplier) };
    });
  }

  const style: React.CSSProperties = { height: "100%" };
  if (autoWidth) {
    // a little extra to account for possible scrollbar width
    style.width = sumBy(columns, "width") + 20;
  }

  return (
    <div style={style}>
      <AdjustColumnsModal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        allColumns={allColumns}
        changeColumnAttribute={changeColumnAttribute}
      />
      <Table
        data={data}
        cellStyle={cellStyle}
        columns={columns}
        getCellLines={getCellLines}
        meet={meet}
        numberOfFixedLeftColumns={numberOfFixedLeftColumns}
        media={media}
        padBottom={padBottom}
        scrollToRow={scrollToRow}
        scrollToAlignment={scrollToAlignment}
        gridAriaLabel={gridAriaLabel}
      />
    </div>
  );
}

export default AdjustableColumnsTableV2;
